import React from 'react'
import { Experiment, Variation } from '@deal/experiment-js'
import { Link } from 'react-router-dom'
import { Button } from '@deal/components'
import Check from './check.svg'
import { DepartmentType } from '#src/generated/graphql'
import styles from './styles.css'

interface Props {
  departmentType?: DepartmentType | null
  onSubmit: () => void
  conversationId: string
  expertFirstName?: string | null
  isPlaceholderExpert: boolean | null
}

interface CurationCopyProps {
  departmentType?: DepartmentType | null
  isPlaceholderExpert: boolean | null
  expertFirstName?: string | null
}

const CurationTitle: React.SFC<CurationCopyProps> = ({
  departmentType,
  isPlaceholderExpert,
  expertFirstName
}) => {
  if (isPlaceholderExpert) {
    return <>We're finding your expert!</>
  }

  switch (departmentType) {
    case DepartmentType.GOLF:
      return <>Talk with {expertFirstName} to find your perfect clubs!</>

    case DepartmentType.YACHT_CHARTERS:
      return <>Talk with {expertFirstName} to plan your perfect charter!</>

    case DepartmentType.CYCLING:
      return <>Talk with {expertFirstName} to find your perfect ride!</>

    case DepartmentType.WINTER_SPORTS:
      return <>Talk with {expertFirstName} to find your perfect gear!</>

    case DepartmentType.CAMPING:
      return <>Talk with {expertFirstName} to find the outdoor gear for your adventure!</>

    default:
      return <>Talk with {expertFirstName}!</>
  }
}

const CurationBody: React.SFC<CurationCopyProps> = ({
  isPlaceholderExpert,
  departmentType,
  expertFirstName
}) => {
  if (isPlaceholderExpert) {
    return (
      <>
        You’re just moments away from talking with your expert and getting hand-picked
        recommendations. In the meantime, feel free to ask us any questions!
      </>
    )
  }

  switch (departmentType) {
    case DepartmentType.GOLF:
      return (
        <>
          {expertFirstName} is ready to answer questions, give insights, and send personalized
          recommendations. Introduce yourself to get started!
        </>
      )

    case DepartmentType.YACHT_CHARTERS:
      return (
        <>
          {expertFirstName} is ready to answer questions, give insights, and send personalized
          itineraries. Introduce yourself to get started!
        </>
      )

    case DepartmentType.CYCLING:
      return (
        <>
          {expertFirstName} is ready to answer questions, give insights, and send personalized
          recommendations. Introduce yourself to get started!
        </>
      )

    case DepartmentType.WINTER_SPORTS:
      return (
        <>
          {expertFirstName} is ready to answer questions, give insights, and send personalized
          recommendations. Introduce yourself to get started!{' '}
        </>
      )

    case DepartmentType.CAMPING:
      return (
        <>
          {expertFirstName} is ready to answer questions and send personalized outdoor gear
          recommendations. Say hi to get started!
        </>
      )

    default:
      return (
        <>
          {expertFirstName} is ready to answer questions, give insights, and send personalized
          recommendations. Introduce yourself to get started!
        </>
      )
  }
}

const EmptyCurations: React.SFC<Props> = ({
  expertFirstName,
  departmentType,
  onSubmit,
  conversationId,
  isPlaceholderExpert
}) => (
  <>
    <div className={styles.flex}>
      <div className={styles.bullets}>
        <span className={styles.bulletActive}>
          <Check className={styles.check} width={10} />
        </span>
        <span className={styles.separator} />
        <span className={styles.bulletCurrent} />
        <span className={styles.separator} />
        <span className={styles.bulletInactive} />
      </div>
      <div className={styles.smallTitle}>
        {isPlaceholderExpert ? 'Match with an expert' : 'Meet your expert'}
      </div>
    </div>
    <div className={styles.title}>
      <CurationTitle
        departmentType={departmentType}
        isPlaceholderExpert={isPlaceholderExpert}
        expertFirstName={expertFirstName}
      />
    </div>
    <div className={styles.subTitle}>
      <CurationBody
        departmentType={departmentType}
        isPlaceholderExpert={isPlaceholderExpert}
        expertFirstName={expertFirstName}
      />
    </div>
    <div className={styles.buttonContainer}>
      <Link to={`/inbox/${conversationId}`}>
        <Button className={styles.button} onClick={onSubmit}>
          {isPlaceholderExpert ? (
            `Connect with us`
          ) : (
            <Experiment id="empty-curation-connect-copy" default="default">
              <Variation id="default">
                {`Connect with ${expertFirstName || 'your expert'}`}
              </Variation>
              <Variation id="v1">Say hi</Variation>
              <Variation id="v2">{`Meet ${expertFirstName || 'your expert'}`}</Variation>
            </Experiment>
          )}
        </Button>
      </Link>
    </div>
  </>
)

export default EmptyCurations
